import { formatDate } from "@/lib/utils";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Download,
  Filter,
  Store,
  RefreshCcw,
  Loader2,
  Calendar,
  Printer as PrintIcon,
  CheckCircle,
  Activity,
  ArrowRightLeft,
  FileText,
  DollarSign,
  Package,
} from "lucide-react";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const StockTransferReport = () => {
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    source_store_id: "",
    destination_store_id: "",
    status: [],
    sort_by: "",
    sort_direction: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const [sourceStoreSearch, setSourceStoreSearch] = useState("");
  const [debouncedSourceStoreSearch, setDebouncedSourceStoreSearch] =
    useState("");
  const [destStoreSearch, setDestStoreSearch] = useState("");
  const [debouncedDestStoreSearch, setDebouncedDestStoreSearch] = useState("");

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSourceStoreSearch(sourceStoreSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [sourceStoreSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDestStoreSearch(destStoreSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [destStoreSearch]);


  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const summaryStats = previewData?.summary || null;

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  const { data: sourceStores = [], isFetching: isSourceStoresFetching } =
    useQuery({
      enabled: hasPermission("report stock transfer list view"),
      queryKey: ["stores", debouncedSourceStoreSearch],
      queryFn: async () => {
        const params = {};
        if (debouncedSourceStoreSearch)
          params.search = debouncedSourceStoreSearch;
        const response = await apiClient.get(`${API_BASE_URL}/store`, {
          params,
        });
        return response.data.data;
      },
    });

  const { data: destStores = [], isFetching: isDestStoresFetching } = useQuery({
    enabled: hasPermission("report stock transfer list view"),
    queryKey: ["stores", debouncedDestStoreSearch],
    queryFn: async () => {
      const params = {};
      if (debouncedDestStoreSearch) params.search = debouncedDestStoreSearch;
      const response = await apiClient.get(`${API_BASE_URL}/store`, {
        params,
      });
      return response.data.data;
    },
  });

  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report stock transfer list view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.source_store_id)
        params.source_store_id = currentFilters.source_store_id;
      if (currentFilters.destination_store_id)
        params.destination_store_id = currentFilters.destination_store_id;
      if (currentFilters.date_from)
        params.date_from = new Date(currentFilters.date_from).toISOString();
      if (currentFilters.date_to)
        params.date_to = new Date(currentFilters.date_to).toISOString();
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_direction)
        params.sort_direction = currentFilters.sort_direction;
      if (currentFilters.status && currentFilters.status.length > 0) {
        params.status = currentFilters.status;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.STOCK_TRANSFER,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report stock transfer list view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.source_store_id)
        params.source_store_id = currentFilters.source_store_id;
      if (currentFilters.destination_store_id)
        params.destination_store_id = currentFilters.destination_store_id;
      if (currentFilters.date_from)
        params.date_from = new Date(currentFilters.date_from).toISOString();
      if (currentFilters.date_to)
        params.date_to = new Date(currentFilters.date_to).toISOString();
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_direction)
        params.sort_direction = currentFilters.sort_direction;
      if (currentFilters.status && currentFilters.status.length > 0) {
        params.status = currentFilters.status;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.STOCK_TRANSFER,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Stock Transfer report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate stock transfer report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating stock transfer report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      date_from: "",
      date_to: "",
      source_store_id: "",
      destination_store_id: "",
      status: [],
      sort_by: "",
      sort_direction: "",
    });
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `stock-transfer-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const sourceStoreOptions = sourceStores.map((store) => ({
    value: String(store.id),
    label: store.name,
  }));

  const destStoreOptions = destStores.map((store) => ({
    value: String(store.id),
    label: store.name,
  }));

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "in_transit", label: "In Transit" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "received", label: "Received" },
  ];

  const sortByOptions = [
    { value: "transfer_number", label: "Transfer Number" },
    { value: "source_store_id", label: "Source Store" },
    { value: "destination_store_id", label: "Destination Store" },
    { value: "status", label: "Status" },
    { value: "total_value", label: "Total Value" },
    { value: "created_at", label: "Date Created" },
  ];

  const sortDirectionOptions = [
    { value: "asc", label: "Ascending" },
    { value: "desc", label: "Descending" },
  ];

  if (!hasPermission("report stock transfer list view")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <ArrowRightLeft className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Inventory Management
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Stock Transfer Report
              </span>
            </div>
          }
          subtitle="Generate and analyze stock transfers between locations."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report stock transfer list view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report stock transfer list view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Filtering Criteria</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Define parameters for your stock transfer analysis
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>
          {showFilters && (
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Store className="h-4 w-4 text-primary" /> Locations
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Source Store
                      </Label>
                      <ReactSelect
                        options={sourceStoreOptions}
                        isLoading={isSourceStoresFetching}
                        value={sourceStoreOptions.find(
                          (opt) =>
                            opt.value === String(filters.source_store_id),
                        )}
                        onInputChange={(val, { action }) => {
                          if (action === "input-change") {
                            setSourceStoreSearch(val);
                          }
                        }}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            source_store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Destination Store
                      </Label>
                      <ReactSelect
                        options={destStoreOptions}
                        isLoading={isDestStoresFetching}
                        value={destStoreOptions.find(
                          (opt) =>
                            opt.value === String(filters.destination_store_id),
                        )}
                        onInputChange={(val, { action }) => {
                          if (action === "input-change") {
                            setDestStoreSearch(val);
                          }
                        }}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            destination_store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Timeframe &
                    Sorting
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          From Date
                        </Label>
                        <Input
                          type="date"
                          name="date_from"
                          value={filters.date_from}
                          onChange={handleFilterChange}
                          className="bg-muted/30"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">To Date</Label>
                        <Input
                          type="date"
                          name="date_to"
                          value={filters.date_to}
                          onChange={handleFilterChange}
                          className="bg-muted/30"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Status</Label>
                      <ReactSelect
                        options={statusOptions}
                        isMulti
                        value={statusOptions.filter((opt) =>
                          filters.status.includes(opt.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            status: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        placeholder="Select Status"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Sort By</Label>
                        <ReactSelect
                          options={sortByOptions}
                          value={sortByOptions.find(
                            (opt) => opt.value === filters.sort_by,
                          )}
                          onChange={(opt) =>
                            setFilters((p) => ({
                              ...p,
                              sort_by: opt?.value || "",
                            }))
                          }
                          placeholder="Sort by"
                          isClearable
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Direction
                        </Label>
                        <ReactSelect
                          options={sortDirectionOptions}
                          value={sortDirectionOptions.find(
                            (opt) => opt.value === filters.sort_direction,
                          )}
                          onChange={(opt) =>
                            setFilters((p) => ({
                              ...p,
                              sort_direction: opt?.value || "",
                            }))
                          }
                          placeholder="Direction"
                          isClearable
                          isDisabled={!filters.sort_by}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <>
            {summaryStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="shadow-sm border-border/50 bg-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Total Transfers
                      </p>
                      <ArrowRightLeft className="h-4 w-4 text-primary" />
                    </div>
                    <div className="text-2xl font-bold">
                      {summaryStats.total_transfers}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-border/50 bg-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Total Value
                      </p>
                      <DollarSign className="h-4 w-4 text-success" />
                    </div>
                    <div className="text-2xl font-bold text-success">
                      {currencySymbol}
                      {parseFloat(summaryStats.total_value || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-border/50 bg-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Processed Transfers
                      </p>
                      <CheckCircle className="h-4 w-4 text-success" />
                    </div>
                    <div className="text-2xl font-bold">
                      {summaryStats.processed_count}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-border/50 bg-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Pending Transfers
                      </p>
                      <Activity className="h-4 w-4 text-warning" />
                    </div>
                    <div className="text-2xl font-bold">
                      {summaryStats.pending_count}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="shadow-none border border-border/50 overflow-hidden">
              <CardHeader className="bg-muted/30 border-b py-4">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Report Data
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="font-semibold text-foreground">
                          Date
                        </TableHead>
                        <TableHead className="font-semibold text-foreground">
                          Transfer #
                        </TableHead>
                        <TableHead className="font-semibold text-foreground">
                          Source
                        </TableHead>
                        <TableHead className="font-semibold text-foreground">
                          Destination
                        </TableHead>
                        <TableHead className="font-semibold text-foreground">
                          Creator
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-center">
                          Items
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-center">
                          Status
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Value
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.data && previewData.data.length > 0 ? (
                        previewData.data.map((item) => (
                          <TableRow
                            key={item.id}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="whitespace-nowrap">
                              {item.created_at
                                ? formatDate(item.created_at)
                                : "-"}
                            </TableCell>
                            <TableCell className="font-medium">
                              {item.transfer_number}
                            </TableCell>
                            <TableCell>
                              {item.source_store?.name || "-"}
                            </TableCell>
                            <TableCell>
                              {item.destination_store?.name || "-"}
                            </TableCell>
                            <TableCell>{item.creator?.name || "-"}</TableCell>
                            <TableCell className="text-center">
                              {item.total_items}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {item.status_label || item.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {currencySymbol}
                              {parseFloat(item.total_value || 0).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="h-32 text-center text-muted-foreground"
                          >
                            <div className="flex flex-col items-center justify-center gap-2">
                              <FileText className="h-8 w-8 text-muted-foreground/40" />
                              <p>
                                No transfers found for the selected criteria.
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Compiled!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Your Stock Transfer report has been successfully generated and
                  is ready for download or printing.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
